import React, { lazy, Component } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, BoxIcon, GlobeIcon } from 'lucide-react';
import SaveToTracker from '../UI/SaveToTracker';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { handleImgError } from '../../utils/imageUtils';
import { FeaturedBookBox } from '../../data/homePageData';
interface FeaturedBookBoxCardProps {
  box: FeaturedBookBox;
}
export const FeaturedBookBoxCard: React.FC<FeaturedBookBoxCardProps> = ({
  box
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const themes = box.theme.split(',').map(t => t.trim());
  return <div className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px]" data-id="element-1789" data-mp="3v8y1u66lvac7">
      <div className={`group rounded-xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-1 h-full flex flex-col ${dk.card}`} data-id="element-1790" data-mp="3vb2djaaqxdn1">
        {/* Image Area */}
        <div className={`aspect-square relative flex items-center justify-center ${isAfterTide ? 'bg-gradient-to-br from-[#2d1b3d] to-[#1a1a2e]' : 'bg-gradient-to-br from-indigo-500 to-violet-600'}`} data-id="element-1791" data-mp="3po7n8qqxjyif">
          {box.image ? <img src={box.image} alt={box.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={handleImgError} data-id="element-1792" data-mp="7grse6oys7ue" /> : <BoxIcon className="h-16 w-16 text-white/50" data-id="element-1793" data-mp="3fvs2jlo90vtu" />}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-1794" data-mp="3fugyrluszo2q"></div>
          <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full border border-white/10 flex items-center gap-1" data-id="element-1795" data-mp="2o5dqzw3pjvlc">
            {box.shipping === 'Worldwide' ? 'Worldwide' : box.shipping}
          </div>
        </div>

        {/* Content — fixed height structure */}
        <div className="p-4 flex-1 flex flex-col min-h-[160px]" data-id="element-1796" data-mp="3qhqu4jaajni6">
          <div className="flex items-center justify-between mb-1" data-id="element-1797" data-mp="91sesiou0lyk">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${rt.textPrimary}`} data-id="element-1798" data-mp="944uep74ak8g">
              {box.frequency}
            </div>
            <SaveToTracker productId={box.id} data-id="element-1799" data-mp="3cjaoyws2u525" />
          </div>
          <Link to={`/book-boxes/${encodeURIComponent(box.name)}`} className={`block text-sm sm:text-base font-bold mb-2 line-clamp-2 h-10 transition-colors ${dk.textHeading} ${rt.textHover}`} data-id="element-1800" data-mp="37k7gt3gsvipy">
            {box.name}
          </Link>
          <div className="flex flex-wrap gap-1.5 mb-3 h-6 overflow-hidden" data-id="element-1801" data-mp="3hk8410hsiizf">
            {themes.slice(0, 3).map((theme, idx) => <span key={idx} className={`text-[10px] px-2 py-0.5 rounded-full border ${dk.badgeNeutral} ${dk.borderSubtle}`} data-id="element-1802" data-mp="ws63bz87k2oe" data-mpi={idx}>
                {theme}
              </span>)}
          </div>
          <div className={`flex items-center text-xs mb-3 ${dk.textMuted}`} data-id="element-1803" data-mp="19bjtljljay47">
            <GlobeIcon className="h-3 w-3 mr-1" data-id="element-1804" data-mp="ue9hc812ho7f" />
            {box.locationCountry}
          </div>
          <div className="mt-auto" data-id="element-1805" data-mp="zcu9dk6611ks">
            <div className={`h-px w-full mb-3 ${dk.border}`} data-id="element-1806" data-mp="zdvvcjopz8ha"></div>
            <div className="flex items-center justify-between" data-id="element-1807" data-mp="1n25efdujti4q">
              <div className={`font-bold ${dk.textHeading}`} data-id="element-1808" data-mp="1s19os7t7llwn">${box.price}</div>
              {box.rating && <div className="flex items-center text-xs font-medium text-amber-400" data-id="element-1809" data-mp="1daslsk0xenxn">
                  <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-1810" data-mp="1ia5730c5fmy4" />
                  {box.rating}
                  <span className={`ml-1 ${dk.textFaint}`} data-id="element-1811" data-mp="26098r25s1cmh">
                    ({box.reviewCount})
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};